import * as d3 from 'd3'

import React from 'react'

import './SeatHistogram.scss'

const MAX_SEATS = 338

interface IProps {
  resultCountData: IResultCount[]
}

export interface IResultCount {
  party: string
  color: string
  seats: number
  count: number
}

class SeatHistogram extends React.Component<IProps> {
  protected svgRef: React.RefObject<SVGSVGElement>

  protected svg?: d3.Selection<SVGSVGElement, IResultCount, null, undefined>
  protected wrapper?: d3.Selection<SVGGElement, IResultCount, null, undefined>
  protected tooltip?: d3.Selection<HTMLDivElement, any, HTMLElement, any>

  protected margin: { top: number; right: number; bottom: number; left: number }
  protected height: number = 400
  protected width: number = 1235

  public constructor(props: IProps) {
    super(props)

    this.margin = { top: 5, right: 15, bottom: 30, left: 40 }

    this.svgRef = React.createRef()

    this.updateSizes = this.updateSizes.bind(this)
    this.setBarSizes = this.setBarSizes.bind(this)
    this.setMajorityLineSize = this.setMajorityLineSize.bind(this)
    this.setMajorityTextSize = this.setMajorityTextSize.bind(this)
  }

  public componentDidMount(): void {
    this.buildGraphic()
    this.updateSizes()
    this.updateGraphic()
  }

  public componentDidUpdate(): void {
    if (this.props.resultCountData.length === 0) {
      this.resetGraphic()
      this.updateSizes()
      this.updateGraphic()
    } else {
      this.updateSizes()
      this.updateGraphic()
    }
  }

  protected buildGraphic(): void {
    if (!this.svgRef.current) return

    this.svg = d3.select(this.svgRef.current)

    this.wrapper = this.svg
      .append('g')
      .attr('class', 'wrapper')
      .attr(
        'transform',
        'translate(' + this.margin.left + ',' + this.margin.top + ')'
      )

    this.wrapper.append('g').attr('class', 'y-axis')

    this.wrapper.append('g').attr('class', 'x-axis')

    this.wrapper
      .append('text')
      .attr('class', 'axis-label y-axis-label')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - this.margin.left + 10)
      .attr('dy', 0)
      .style('text-anchor', 'middle')
      .text('Number of outcomes')

    this.wrapper
      .append('text')
      .attr('class', 'axis-label x-axis-label')
      .style('text-anchor', 'middle')
      .text('Number of seats')

    this.updateSizes()

    // build tooltip
    this.tooltip = d3
      .select('div#SeatHistogram')
      .append('div')
      .attr('class', 'tool-tip')

    window.addEventListener('resize', (): void => {
      this.updateSizes()
      this.updateVizComponents()
    })
  }

  protected updateSizes(): void {
    const width = this.svgRef.current
      ? this.svgRef.current.getBoundingClientRect().width
      : 1235

    const height = Math.min(width * 0.5, 400)

    // set dimensions and margins of graph
    this.width = width - this.margin.left - this.margin.right
    this.height = height - this.margin.top - this.margin.bottom

    if (!(this.svg && this.wrapper)) return

    this.svg
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)

    this.wrapper
      .select('.x-axis')
      .attr('transform', 'translate(0,' + this.height + ')')

    this.wrapper
      .select('.x-axis-label')
      .attr('x', this.width / 2)
      .attr('y', this.height + this.margin.bottom)

    this.wrapper.select('.y-axis-label').attr('x', 0 - this.height / 2)
  }

  protected getXScale(): d3.ScaleLinear<number, number> {
    // const maxSeats =
    //   d3.max(this.props.resultCountData, (d): number => d.seats) || 1
    // console.log('maxSeats', maxSeats)
    return d3
      .scaleLinear()
      .range([0, this.width])
      .domain([0, MAX_SEATS])
  }

  protected getYScale(): d3.ScalePower<number, number> {
    const maxCount =
      d3.max(this.props.resultCountData, (d): number => d.count) || 1
    return d3
      .scalePow()
      .exponent(0.5)
      .range([this.height, 0])
      .domain([0, maxCount])
  }

  protected updateVizComponents(): void {
    if (!this.svg) return

    this.svg
      .selectAll('.bar')
      .transition()
      .duration(500)
      .call(this.setBarSizes as any)
    this.svg
      .selectAll('rect.majority')
      .transition()
      .duration(500)
      .call(this.setMajorityLineSize as any)
    this.svg
      .selectAll('text.majority')
      .transition()
      .duration(500)
      .call(this.setMajorityTextSize as any)

    const yAxis = this.svg.select('.y-axis')
    const xAxis = this.svg.select('.x-axis')

    // TODO: fix
    yAxis
      .transition()
      .duration(500)
      .call(d3.axisLeft(this.getYScale()).ticks(5) as any)

    xAxis
      .transition()
      .duration(500)
      .call(d3.axisBottom(this.getXScale()).ticks(10) as any)

    if (this.props.resultCountData.length > 0) {
      yAxis.style('opacity', 1)
      xAxis.style('opacity', 1)
    } else {
      yAxis.style('opacity', 0)
      xAxis.style('opacity', 0)
    }
  }

  protected setBarSizes(bars: d3.Selection<any, any, any, any>): void {
    const barWidth = this.width / MAX_SEATS
    bars
      .attr(
        'x',
        (d: IResultCount): number => this.getXScale()(d.seats) - barWidth / 2
      )
      .attr('y', (d: IResultCount): number => this.getYScale()(d.count))
      .attr('width', barWidth)
      .attr(
        'height',
        (d: IResultCount): number => this.height - this.getYScale()(d.count)
      )
  }

  protected setMajorityLineSize(
    majorityLine: d3.Selection<any, any, any, any>
  ): void {
    majorityLine
      .attr('x', this.getXScale()(MAX_SEATS / 2) - 0.5)
      .attr('width', 1)
      .attr('y', 0)
      .attr('height', this.height)
  }

  protected setMajorityTextSize(
    majorityText: d3.Selection<any, any, any, any>
  ): void {
    majorityText.attr('x', this.getXScale()(MAX_SEATS / 2) + 5).attr('y', 10)
  }

  protected updateGraphic(): void {
    if (!this.wrapper) return

    // define filtered data
    const findSeatCounts = (data: IResultCount): IResultCount[] => {
      const results = this.props.resultCountData.filter((item): boolean => {
        return item.seats === data.seats && item.count >= 1
      })
      if (!results) {
        throw new Error('No result found for this seat count!')
      }
      return results
    }

    // define chart
    var bars = this.wrapper
      .selectAll('.bar')
      .data(
        this.props.resultCountData,
        (d: any): string => `${d.party}${d.seats}`
      )

    // enter: for new data added (data that needs DOM)
    bars
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('fill', (d: IResultCount): string => d.color)
      .attr('opacity', 0.8)
      .call(this.setBarSizes)

    bars
      .on('mouseover', (d: IResultCount): void => {
        // const currentSeatCount = findSeatCount(d)

        // builds array of html content for each result
        const buildHtmlContent = (resultCounts: IResultCount[]): string => {
          return resultCounts
            .map((resultCount: IResultCount): string => {
              const { color, party, seats, count } = resultCount
              return `<b style="color: ${color}">${party}</b> win <b>${seats}</b> seats <b>${count}</b> times <br />`
            })
            .join('')
        }

        if (this.tooltip) {
          const content = buildHtmlContent(findSeatCounts(d))
          this.tooltip
            .style('left', this.margin.left + this.getXScale()(d.seats) + 'px')
            .style('display', 'block')
            .style(
              'bottom',
              this.height - this.getYScale()(d.count) + 40 + 'px'
            )
            // .style('border-color', d.color)
            .html(`<div>${content}</div>`)
        }
      })
      .on('mouseout', (): void => {
        if (this.tooltip) this.tooltip.style('display', 'none')
      })

    bars.exit().remove()

    // draw majority bar
    if (
      this.props.resultCountData.length > 0 &&
      this.wrapper.selectAll('.majority').size() === 0
    ) {
      this.wrapper
        .append('rect')
        .attr('class', 'majority')
        .call(this.setMajorityLineSize)
      this.wrapper
        .append('text')
        .attr('class', 'majority')
        .text('Majority')
        .call(this.setMajorityTextSize)
    }

    this.updateVizComponents()
  }

  protected resetGraphic(): void {
    if (!this.wrapper) return

    this.wrapper.selectAll('.bar').remove()
    this.wrapper.selectAll('.majority').remove()
  }

  public render(): JSX.Element {
    return (
      <div className="row my-3">
        <div className="col" id="SeatHistogram">
          <svg id="supplemental-graph" ref={this.svgRef} />
        </div>
      </div>
    )
  }
}

export default SeatHistogram
